import { NIGHT_FROM_HOUR, NIGHT_TO_HOUR, type ReportNight } from "../../lib/report.ts";
import { shortDuration, weekdayDay } from "../../lib/time.ts";
import { cn } from "../../lib/utils.ts";
import { TICKS } from "../overview/sleep-chart.tsx";

/** Columns: the night, its noon-to-noon bar, then (from a small tablet, and on paper) its numbers. */
const COLS =
  "grid grid-cols-[48px_minmax(0,1fr)] gap-x-2 sm:grid-cols-[56px_minmax(0,1fr)_repeat(4,52px)] print:grid-cols-[56px_minmax(0,1fr)_repeat(4,52px)]";
const NUMBER = "hidden text-right sm:block print:block";
/** The shaded 7 pm – 7 am band, as fractions of the noon-to-noon row. */
const NIGHT_LEFT = (NIGHT_FROM_HOUR - 12) / 24;
const NIGHT_WIDTH = (24 - NIGHT_FROM_HOUR + NIGHT_TO_HOUR) / 24;

const cell = (n: ReportNight, ms: number) => (n.segments.length ? shortDuration(ms) : "—");

/**
 * The report's sleep: one row per night, noon to noon, as the overview's sleep
 * window drawn still for paper. Night (7 pm – 7 am) is shaded, the longest
 * stretch solid and other sleep a lighter wash; beside each row its total,
 * night and day split, and longest stretch. On a phone the numbers sit under
 * the bar instead.
 */
export function SleepNights({ nights }: { nights: ReportNight[] }) {
  if (!nights.some((n) => n.segments.length))
    return <p className="py-3 text-[14px] italic opacity-60">No sleep logged.</p>;
  return (
    <figure className="print-exact mt-3">
      <div
        className={cn(COLS, "text-[10px] uppercase tracking-[0.06em] opacity-55")}
        aria-hidden="true"
      >
        <span />
        <span className="relative h-3">
          {TICKS.map((t) => (
            <span
              key={t.at}
              className={cn(
                "absolute top-0 whitespace-nowrap",
                t.at === 0 ? "left-0" : t.at === 1 ? "right-0" : "-translate-x-1/2",
              )}
              style={t.at > 0 && t.at < 1 ? { left: `${t.at * 100}%` } : undefined}
            >
              {t.label}
            </span>
          ))}
        </span>
        <span className={NUMBER}>Total</span>
        <span className={NUMBER}>Night</span>
        <span className={NUMBER}>Day</span>
        <span className={NUMBER}>Longest</span>
      </div>
      <ul className="mt-1 text-[12px] tabular-nums">
        {nights.map((n) => (
          <li
            key={n.start.getTime()}
            className={cn(
              COLS,
              "break-inside-avoid items-center border-t border-rule py-1",
              !n.counted && "opacity-60",
            )}
          >
            <span className="sr-only">{describe(n)}</span>
            <span className="leading-tight" aria-hidden="true">
              {weekdayDay(n.start)}
              {n.inProgress ? <span className="block text-[10px] italic">so far</span> : null}
            </span>
            <span className="relative h-3.5" aria-hidden="true">
              <span
                className="absolute inset-y-0 bg-rule"
                style={{ left: `${NIGHT_LEFT * 100}%`, width: `${NIGHT_WIDTH * 100}%` }}
              />
              {[0.25, 0.5, 0.75].map((at) => (
                <span
                  key={at}
                  className="absolute inset-y-0 w-px bg-rule"
                  style={{ left: `${at * 100}%` }}
                />
              ))}
              <span className="absolute inset-x-0 bottom-0 h-px bg-rule" />
              {n.segments.map((s) => (
                <span
                  key={s.id}
                  className={cn(
                    "absolute inset-y-0 min-w-[2px] rounded-[1px] bg-chart-sleep",
                    !s.main && "opacity-45",
                  )}
                  style={{ left: `${s.start * 100}%`, width: `${(s.end - s.start) * 100}%` }}
                />
              ))}
            </span>
            <span className={NUMBER} aria-hidden="true">
              {cell(n, n.totalMs)}
            </span>
            <span className={NUMBER} aria-hidden="true">
              {cell(n, n.nightMs)}
            </span>
            <span className={NUMBER} aria-hidden="true">
              {cell(n, n.dayMs)}
            </span>
            <span className={cn(NUMBER, "heading")} aria-hidden="true">
              {n.longest ? shortDuration(n.longest.ms) : "—"}
            </span>
            <span
              className="col-start-2 mt-0.5 text-[11px] opacity-75 sm:hidden print:hidden"
              aria-hidden="true"
            >
              {n.segments.length
                ? `${shortDuration(n.totalMs)} · night ${shortDuration(n.nightMs)} · day ${shortDuration(n.dayMs)}${n.longest ? ` · longest ${shortDuration(n.longest.ms)}` : ""}`
                : "No sleep logged"}
            </span>
          </li>
        ))}
      </ul>
    </figure>
  );
}

/** The row read aloud: "Mon 28 night: 14h 10m asleep, 9h 30m at night, …". */
function describe(n: ReportNight): string {
  const night = `${weekdayDay(n.start)} night${n.inProgress ? " so far" : ""}`;
  if (!n.segments.length) return `${night}: no sleep logged`;
  const parts = [
    `${shortDuration(n.totalMs)} asleep`,
    `${shortDuration(n.nightMs)} at night`,
    `${shortDuration(n.dayMs)} by day`,
  ];
  if (n.longest) parts.push(`longest stretch ${shortDuration(n.longest.ms)}`);
  return `${night}: ${parts.join(", ")}`;
}
